// ============================================================================
// CardRenderer — switches a render payload to its card component
// Unknown render.type degrades gracefully to the plain ToolCallCard pill
// (except for staff, who already see the raw pill in MessageBubble).
// ============================================================================

import type { RenderPayload } from '@/types/chatRenders'
import type { WidgetPreview } from '@/hooks/useChat'
import ToolCallCard from '../ToolCallCard'
import { isKnownRenderType } from './cardUtils'
import ReceiptCard from './ReceiptCard'
import WorkoutCard from './WorkoutCard'
import InsightChartCard from './InsightChartCard'
import TrainingWeekCard from './TrainingWeekCard'
import MemoryCard from './MemoryCard'
import RoutineCard from './RoutineCard'
import DocCard from './DocCard'

interface CardRendererProps {
  render: RenderPayload
  /** Staff see the raw ToolCallCard pill alongside cards (debug view), so the
   *  unknown-type fallback pill is suppressed for them to avoid duplicates. */
  isStaff?: boolean
  /** True when the render arrived live in this session (drives the memory
   *  card's learning-moment animation). */
  live?: boolean
  /** The message's widget previews — a `chart` render whose code rode on the
   *  tool result finds it here and skips the refetch. */
  widgets?: WidgetPreview[]
}

export default function CardRenderer({ render, isStaff = false, live = false, widgets }: CardRendererProps) {
  switch (render.type) {
    case 'receipt':
      return <ReceiptCard render={render} />
    case 'workout_card':
      return <WorkoutCard render={render} />
    case 'chart': {
      const widgetId = render.spec.widget_id
      const preview = widgets?.find(w => w.fromRender && w.widgetId === String(widgetId))
      return <InsightChartCard render={render} code={preview?.code} />
    }
    case 'training_week':
      return <TrainingWeekCard render={render} />
    case 'memory':
      return <MemoryCard render={render} animate={live} />
    case 'routine':
      return <RoutineCard render={render} />
    case 'doc':
      return <DocCard render={render} />
    default: {
      // Graceful degradation: unknown render type -> today's pill, exactly as
      // if no render had been attached. isKnownRenderType guards future drift.
      if (isKnownRenderType(render.type) || isStaff) return null
      return (
        <ToolCallCard
          toolCall={{
            tool: render.tool ?? (render.entity.kind || 'tool'),
            args: {},
            status: 'complete',
          }}
          isStaff={isStaff}
        />
      )
    }
  }
}
